import React, { useState, useEffect } from 'react';
import {Layout, Typography, Avatar, Dropdown, Space, Switch} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { usePageTitle } from '../../context/PageTitleContext';
import { useTheme } from '../../../main/context/ThemeContext';
import {
    deviceAuthService,
    deviceCashierRoleService,
    deviceCashierService,
    deviceRegisterService
} from '../../services/posApi';
import { useProductsSync } from '../../hooks/useProductsSync';
import './PosAppHeader.css';
import CalculatorModal from "../CalculatorModal";
import ShiftInfoModal from "../ShiftInfoModal";
import CashTrackingModal from "../CashTrackingModal";



const { Header } = Layout;
const { Title, Text } = Typography;

/**
 * App header component with device authentication support
 */
const PosAppHeader = () => {
    const { pageTitle, rightContent } = usePageTitle();
    const { darkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [menuCollapsed, setMenuCollapsed] = useState(false);
    const [isOnline, setIsOnline] = useState(true);
    const [iconColor, setIconColor] = useState('white');
    const [isSystemOnline, setIsSystemOnline] = useState(true);
    const [systemIconColor, setSystemIconColor] = useState('white');
    const [syncMenuVisible, setSyncMenuVisible] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [calculatorVisible, setCalculatorVisible] = useState(false);
    const [shiftInfoVisible, setShiftInfoVisible] = useState(false);
    const [cashTrackingVisible, setCashTrackingVisible] = useState(false);


    // Состояния для авторизации
    const [isDeviceAuthenticated, setIsDeviceAuthenticated] = useState(false);
    const [currentCashier, setCurrentCashier] = useState(null);
    const [registerInfo, setRegisterInfo] = useState(null);

    const { syncProducts, isSyncing: isProductsSyncing } = useProductsSync();
    const [isCashierSyncing, setIsCashierSyncing] = useState(false);
    const [isRegisterSyncing, setIsRegisterSyncing] = useState(false);

    // Проверка состояния авторизации при загрузке
    useEffect(() => {
        checkAuthStatus();
    }, []);

    useEffect(() => {
        // Слушаем изменения в localStorage для обновления состояния кассира
        const handleStorageChange = (e) => {
            if (e.key === 'device_cashier_info') {
                checkAuthStatus();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Также слушаем изменения в том же окне
        const handleLocalStorageUpdate = () => {
            checkAuthStatus();
        };

        // Кастомное событие для обновления в том же окне
        window.addEventListener('cashierStatusUpdate', handleLocalStorageUpdate);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('cashierStatusUpdate', handleLocalStorageUpdate);
        };
    }, []);

    const checkAuthStatus = async () => {
        // Проверяем авторизацию устройства
        const deviceAuth = deviceAuthService.isAuthenticated();
        setIsDeviceAuthenticated(deviceAuth);

        if (deviceAuth) {
            try {
                // Получаем информацию о кассире из localStorage
                const cashierData = localStorage.getItem('device_cashier_info');
                if (cashierData) {
                    const parsedData = JSON.parse(cashierData);
                    if (parsedData.cashier_authorized && parsedData.cashier) {
                        setCurrentCashier(parsedData.cashier);
                    } else {
                        setCurrentCashier(null);
                    }
                } else {
                    setCurrentCashier(null);
                }

                // Получаем информацию об устройстве
                const savedRegisterInfo = deviceRegisterService.getSavedRegisterInfo();
                if (savedRegisterInfo) {
                    setRegisterInfo(savedRegisterInfo);
                } else {
                    try {
                        const registerData = await deviceRegisterService.getRegisterInfo();
                        setRegisterInfo(registerData);
                    } catch (error) {
                        console.error('Ошибка получения информации об устройстве:', error);
                    }
                }
            } catch (error) {
                console.error('Ошибка при проверке статуса авторизации:', error);
                setCurrentCashier(null);
            }
        } else {
            // Если устройство не авторизовано, очищаем все данные
            setCurrentCashier(null);
            setRegisterInfo(null);
        }
    };

    const handleProductsSync = async () => {
        try {
            console.log('Запуск ручной синхронизации товаров...');
            await syncProducts(true);
            console.log('Синхронизация товаров завершена');
        } catch (error) {
            console.error('Ошибка синхронизации товаров:', error);
        }
    };

    const handleCashierSync = async () => {
        if (!currentCashier) return;

        try {
            setIsCashierSyncing(true);
            console.log('Запуск синхронизации кассира...');

            const cashierStatus = await deviceCashierService.getCashierStatus();
            localStorage.setItem('device_cashier_info', JSON.stringify(cashierStatus));

            // Обновляем состояние
            checkAuthStatus();

            console.log('Синхронизация кассира завершена');
        } catch (error) {
            console.error('Ошибка синхронизации кассира:', error);
        } finally {
            setIsCashierSyncing(false);
        }
    };

    const handleRegisterSync = async () => {
        try {
            setIsRegisterSyncing(true);
            console.log('Запуск синхронизации кассы...');

            const registerInfo = await deviceRegisterService.getRegisterInfo();
            localStorage.setItem('device_register_info', JSON.stringify(registerInfo));

            // Обновляем состояние
            setRegisterInfo(registerInfo);

            console.log('Синхронизация кассы завершена');
        } catch (error) {
            console.error('Ошибка синхронизации кассы:', error);
        } finally {
            setIsRegisterSyncing(false);
        }
    };

    // Функция генерации UUID (добавить в imports или в начало компонента)
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Функция для выхода кассира (НЕ устройства)
    const handleLogout = async () => {
        try {
            if (currentCashier) {
                const correlationKey = generateUUID();
                const idempotencyKey = generateUUID();
                await deviceCashierService.logoutCashier(
                    currentCashier.uuid,
                    correlationKey,
                    idempotencyKey
                );
            }

            // Очищаем состояние кассира
            setCurrentCashier(null);

            // Даем время на обновление состояния перед навигацией
            setTimeout(() => {
                navigate('/device/cashier-login');
            }, 100);

        } catch (error) {
            console.error('Ошибка при выходе кассира:', error);
            // В случае ошибки тоже очищаем кассира
            setCurrentCashier(null);
            setTimeout(() => {
                navigate('/device/cashier-login');
            }, 100);
        }
    };

    // Функция для проверки интернет-соединения
    const checkInternetConnection = () => {
        setIsOnline(navigator.onLine);
    };

    // Форматирование даты
    const formatDate = (date) => {
        const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
        const months = [
            'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
            'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
        ];

        const dayName = days[date.getDay()];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${dayName}, ${day} ${month} ${hours}:${minutes}`;
    };

    const formatUserName = (cashier) => {
        if (!cashier?.f_name) return 'Гость';
        const firstName = cashier.f_name || '';
        const lastName = cashier.l_name || '';

        if (lastName.length > 1) {
            return `${firstName} ${lastName.charAt(0)}.`.trim();
        }
        return `${firstName} ${lastName}`.trim();
    };

    // Показываем инициалы на аватарке
    const getInitials = (cashier) => {
        if (!cashier?.f_name) return 'Г';
        const firstInitial = cashier.f_name.charAt(0).toUpperCase();
        const lastInitial = cashier.l_name ? cashier.l_name.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    };

    // Меню для выпадающего списка пользователя
    const userMenu = {
        items: [
            {
                key: '1',
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text strong>
                            {currentCashier
                                ? `${currentCashier.f_name} ${currentCashier.l_name || ''}`.trim()
                                : 'Гость'}
                        </Text>
                    </div>
                ),
            },
            {
                key: '2',
                label: (
                    <Space>
                        <Text>{currentCashier?.role || 'Кассир'}</Text>
                    </Space>
                ),
                style: { display: currentCashier ? 'block' : 'none' }
            },
            {
                key: '3',
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {registerInfo?.cash_register.name || 'Касса'}
                        </Text>
                    </div>
                ),
                style: { display: registerInfo ? 'block' : 'none' }
            },
            {
                key: '4',
                label: (
                    <Space>
                        <Text>Тёмная тема</Text>
                        <Switch
                            checked={darkMode}
                            onChange={toggleTheme}
                            size="small"
                        />
                    </Space>
                ),
            },
            {
                type: 'divider',
                key: 'divider',
                style: { display: isDeviceAuthenticated ? 'block' : 'none' }
            },
            {
                key: '5',
                danger: true,
                icon: <LogoutOutlined />,
                label: 'Выйти',
                onClick: handleLogout,
                style: { display: currentCashier ? 'block' : 'none' }
            },
        ],
    };

    // Меню для статуса интернета
    const internetMenu = {
        items: [
            {
                key: '1',
                label: (
                    <div style={{ padding: '8px 0' }}>
                        <Text strong>
                            {isOnline ? 'Интернет подключён' : 'Нет интернета'}
                        </Text>
                    </div>
                ),
            },
        ],
    };

    const syncMenu = {
        onClick: ({ key }) => {
            if (key === 'products' && !isProductsSyncing) {
                handleProductsSync();
            } else if (key === 'cashier' && !isCashierSyncing) {
                handleCashierSync();
            } else if (key === 'register' && !isRegisterSyncing) {
                handleRegisterSync();
            }
        },
        items: [
            {
                key: 'sync-title',
                label: (
                    <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <Text strong>Синхронизация данных</Text>
                    </div>
                ),
                disabled: true
            },
            {
                key: 'products',
                disabled: isProductsSyncing,
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text strong>Товары</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {isProductsSyncing ? 'Синхронизируется...' : 'Нажмите для синхронизации'}
                        </Text>
                    </div>
                ),
            },
            {
                key: 'cashier',
                disabled: isCashierSyncing,
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text strong>Кассир</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {isCashierSyncing ? 'Синхронизируется...' : 'Нажмите для синхронизации'}
                        </Text>
                    </div>
                ),
            },
            {
                key: 'register',
                disabled: isRegisterSyncing,
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text strong>Касса</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {isRegisterSyncing ? 'Синхронизируется...' : 'Нажмите для синхронизации'}
                        </Text>
                    </div>
                ),
            },
        ],
    };

    const systemMenu = {
        items: [
            {
                key: '1',
                label: (
                    <div style={{ padding: '8px 0' }}>
                        <Text strong>
                            {isSystemOnline ? 'Есть связь с системой' : 'Нет связи с системой'}
                        </Text>
                    </div>
                ),
            },
        ],
    };

    // Меню
    const saleMenu = {
        items: [
            {
                key: '1',
                label: <Link to="/device/sale">Новая продажа</Link>,
            },
            {
                key: '2',
                label: <Link to="/device/sales">История продаж</Link>,
            },
        ],
    };

    const shiftMenu = {
        items: [
            {
                key: '1',
                label: 'Трекинг наличных',
                onClick: () => setCashTrackingVisible(true),
            },
            {
                key: '2',
                label: 'Информация о смене',
                onClick: () => setShiftInfoVisible(true),
            }
        ],
    };

    const otherMenu = {
        items: [
            {
                key: '1',
                label: 'Калькулятор',
                onClick: () => setCalculatorVisible(true),
            },
            {
                key: '2',
                label: 'Перезагрузить',
                onClick: () => window.location.reload(),
            },
        ],
    };

    const testMenu = {
        items: [
            {
                key: '1',
                label: <Link to="/device/test">Test</Link>,
            },
            {
                key: '2',
                label: <Link to="/device/test-kaspi">Kaspi Test</Link>,
            },
        ],
    };

    // Слушаем изменения размера окна
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', checkScreenSize);
        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    // Проверка интернета при монтировании и на события online/offline
    useEffect(() => {
        checkInternetConnection();

        window.addEventListener('online', checkInternetConnection);
        window.addEventListener('offline', checkInternetConnection);

        return () => {
            window.removeEventListener('online', checkInternetConnection);
            window.removeEventListener('offline', checkInternetConnection);
        };
    }, []);

    // Мигание иконки при отсутствии интернета
    useEffect(() => {
        let blinkInterval;
        if (!isOnline) {
            blinkInterval = setInterval(() => {
                setIconColor(prev => prev === 'white' ? '#db5c5c' : 'white');
            }, 1000);
        } else {
            setIconColor('white');
        }

        return () => {
            if (blinkInterval) clearInterval(blinkInterval);
        };
    }, [isOnline]);

    useEffect(() => {
        let blinkInterval;
        if (!isSystemOnline) {
            blinkInterval = setInterval(() => {
                setSystemIconColor(prev => prev === 'white' ? '#db5c5c' : 'white');
            }, 1000);
        } else {
            setSystemIconColor('white');
        }

        return () => {
            if (blinkInterval) clearInterval(blinkInterval);
        };
    }, [isSystemOnline]);

    // Обновление времени каждую минуту
    useEffect(() => {
        const timeInterval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);

        return () => clearInterval(timeInterval);
    }, []);

    useEffect(() => {
        const checkSystemConnection = async () => {
            try {
                const response = await fetch('https://api-pos.minimarket116.kz/api/v1/device', {
                    method: 'HEAD',
                    timeout: 5000
                });
                setIsSystemOnline(response.ok);
            } catch (error) {
                setIsSystemOnline(false);
            }
        };

        // Первоначальная проверка
        checkSystemConnection();

        // Интервал проверки: 1 минута если онлайн, 10 секунд если офлайн
        const interval = setInterval(checkSystemConnection, isSystemOnline ? 60000 : 10000);

        return () => clearInterval(interval);
    }, [isSystemOnline]);

    // Определяем, показывать ли логотип
    const showLogo = !isMobile && !menuCollapsed;

    return (
        <>
            <Helmet>
                <title>POS система</title>
            </Helmet>
            <Header className="pos-app-header">
                {/* Left corner SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    className="pos-app-header__corner pos-app-header__corner--left"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 32V0H32C24.5602 0 20.8403 0 17.7883 0.817781C9.50613 3.03699 3.03699 9.50614 0.81778 17.7883C0 20.8403 0 24.5602 0 32Z"
                        fill="currentColor"
                    />
                </svg>

                {/* Left section - Logo */}
                {showLogo && (
                    <div className="pos-app-header__logo-section">
                        <svg
                            width={64}
                            height={24}
                            viewBox="0 0 86 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="pos-app-header__logo"
                        >
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M41.8242 22.9992V7.1592H47.7762C49.4402 7.1592 50.8082 7.6312 51.8802 8.5752C52.9522 9.5192 53.4882 10.7512 53.4882 12.2712C53.4882 13.7752 52.9522 15.0072 51.8802 15.9672C50.8082 16.9272 49.4402 17.4072 47.7762 17.4072H45.0642V22.9992H41.8242ZM45.0642 14.5752H47.7762C48.5282 14.5752 49.1122 14.3672 49.5282 13.9512C49.9602 13.5192 50.1762 12.9592 50.1762 12.2712C50.1762 11.5992 49.9522 11.0552 49.5042 10.6392C49.0722 10.2232 48.4962 10.0152 47.7762 10.0152H45.0642V14.5752Z"
                                  fill="white"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M55.1622 15.0792C55.1302 12.8232 55.9462 10.8792 57.6102 9.2472C59.2902 7.6152 61.2742 6.8232 63.5622 6.8712C65.8502 6.8232 67.8342 7.6152 69.5142 9.2472C71.2102 10.8792 72.0422 12.8232 72.0102 15.0792C72.0422 17.3352 71.2102 19.2792 69.5142 20.9112C67.8342 22.5432 65.8502 23.3352 63.5622 23.2872C61.2742 23.3352 59.2902 22.5432 57.6102 20.9112C55.9462 19.2792 55.1302 17.3352 55.1622 15.0792ZM68.6742 15.0792C68.6742 13.5752 68.1862 12.3352 67.2102 11.3592C66.2502 10.3672 65.0422 9.8712 63.5862 9.8712C62.1142 9.8712 60.8982 10.3592 59.9382 11.3352C58.9782 12.3112 58.4982 13.5592 58.4982 15.0792C58.4982 16.5992 58.9782 17.8472 59.9382 18.8232C60.8982 19.7992 62.1142 20.2872 63.5862 20.2872C65.0422 20.2872 66.2502 19.7992 67.2102 18.8232C68.1862 17.8472 68.6742 16.5992 68.6742 15.0792Z"
                                  fill="white"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M73.6499 19.7832L75.8819 18.1032C77.0019 19.6392 78.2979 20.4072 79.7699 20.4072C80.4739 20.4072 81.0259 20.2552 81.4259 19.9512C81.8259 19.6312 82.0259 19.1992 82.0259 18.6552C82.0259 18.2072 81.8899 17.8632 81.6179 17.6232C81.3459 17.3672 80.8099 17.0552 80.0099 16.6872L78.3059 15.9192C75.7459 14.7832 74.4659 13.2632 74.4659 11.3592C74.4659 10.0152 74.9619 8.9352 75.9539 8.1192C76.9459 7.2872 78.2019 6.8712 79.7219 6.8712C80.9059 6.8712 81.9459 7.1432 82.8419 7.6872C83.7379 8.2152 84.4419 8.9432 84.9539 9.8712L82.7459 11.5752C81.9299 10.3592 80.9219 9.7512 79.7219 9.7512C79.1459 9.7512 78.6739 9.8872 78.3059 10.1592C77.9379 10.4312 77.7539 10.8072 77.7539 11.2872C77.7539 11.6872 77.8819 12.0152 78.1379 12.2712C78.4099 12.5112 78.8979 12.7912 79.6019 13.1112L81.4019 13.9032C82.7619 14.5112 83.7539 15.1752 84.3779 15.8952C85.0019 16.5992 85.3139 17.4792 85.3139 18.5352C85.3139 19.9592 84.7939 21.1112 83.7539 21.9912C82.7139 22.8552 81.3859 23.2872 79.7699 23.2872C78.4419 23.2872 77.2419 22.9752 76.1699 22.3512C75.1139 21.7272 74.2739 20.8712 73.6499 19.7832Z"
                                  fill="white"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M22.7097 0H9.29032C4.15942 0 0 4.15942 0 9.29032V22.7097C0 27.8406 4.15942 32 9.29032 32H22.7097C27.8406 32 32 27.8406 32 22.7097V9.29032C32 4.15942 27.8406 0 22.7097 0Z"
                                  fill="#05D32A"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M25 15.5H7V24C7 24.8284 7.67157 25.5 8.5 25.5H23.5C24.3284 25.5 25 24.8284 25 24V15.5ZM23 20.5V23.5H9V20.5H23ZM28 24C28 26.4853 25.9853 28.5 23.5 28.5H8.5C6.01472 28.5 4 26.4853 4 24V12.5H8V9.5H11V12.5H21V6.5H8V3.5H24V12.5H28V24Z"
                                  fill="#17181A"/>
                        </svg>
                    </div>
                )}

                {/* Center section - Dropdowns (показываем только если устройство авторизовано) */}
                {isDeviceAuthenticated && (
                    <div className="pos-app-header__center-section">
                        <Dropdown
                            menu={saleMenu}
                            placement="bottomLeft"
                            arrow
                            trigger={['click']}
                            autoAdjustOverflow={false}
                            getPopupContainer={() => document.body}
                        >
                            <Text
                                className="pos-app-header__title"
                                style={{ cursor: 'pointer', margin: 0 }}
                            >
                                Продажи
                            </Text>
                        </Dropdown>
                        <Dropdown
                            menu={shiftMenu}
                            placement="bottomLeft"
                            arrow
                            trigger={['click']}
                            autoAdjustOverflow={false}
                            getPopupContainer={() => document.body}
                        >
                            <Text
                                className="pos-app-header__title"
                                style={{ cursor: 'pointer', margin: 0 }}
                            >
                                Смена
                            </Text>
                        </Dropdown>
                        <Dropdown
                            menu={otherMenu}
                            placement="bottomLeft"
                            arrow
                            trigger={['click']}
                            autoAdjustOverflow={false}
                            getPopupContainer={() => document.body}
                        >
                            <Text
                                className="pos-app-header__title"
                                style={{ cursor: 'pointer', margin: 0 }}
                            >
                                Другое
                            </Text>
                        </Dropdown>
                        {deviceCashierRoleService.hasRole('director_store')&&(
                        <Dropdown
                            menu={testMenu}
                            placement="bottomLeft"
                            arrow
                            trigger={['click']}
                            autoAdjustOverflow={false}
                            getPopupContainer={() => document.body}
                        >
                            <Text
                                className="pos-app-header__title"
                                style={{ cursor: 'pointer', margin: 0 }}
                            >
                                Debug
                            </Text>
                        </Dropdown>
                        )}
                        {currentCashier && (
                            <Text
                                className="pos-app-header__title"
                                style={{ cursor: 'pointer', margin: 0 }}
                                onClick={handleLogout}
                            >
                                Выйти
                            </Text>
                        )}
                    </div>
                )}

                {/* Right section - User info block */}
                <div className="pos-app-header__right-section">
                    {/* Иконка интернета с датой (показываем только если устройство авторизовано) */}
                    {isDeviceAuthenticated && (
                        <div className="pos-app-header__status-block">
                            {/* Иконка синхронизации */}
                            <Dropdown
                                menu={syncMenu}
                                placement="bottomRight"
                                arrow
                                trigger={['click']}
                                autoAdjustOverflow={false}
                                getPopupContainer={() => document.body}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    opacity: (isProductsSyncing || isCashierSyncing || isRegisterSyncing) ? 0.7 : 1,
                                    transition: 'transform 1s linear, opacity 0.3s ease',
                                }}>
                                    <svg
                                        version="1.1"
                                        xmlns="http://www.w3.org/2000/svg"
                                        xmlnsXlink="http://www.w3.org/1999/xlink"
                                        viewBox="0 0 17.2818 14.4906"
                                        className="pos-app-header__status-icon"
                                        style={{ marginRight: '8px' }}
                                    >
                                        <g>
                                            <rect height="14.4906" opacity="0" width="17.2818" x="0" y="0"/>
                                            <path d="M8.45335 0C6.56319 0 4.84723 0.745562 3.59995 1.949C3.1402 2.34609 3.21573 2.91771 3.53093 3.24085C3.81376 3.51724 4.26553 3.62187 4.74444 3.22518C5.69852 2.3002 7.00621 1.72163 8.45335 1.72163C11.0274 1.72163 13.1808 3.47433 13.7893 5.84668L12.9766 5.84668C12.4149 5.84668 12.2803 6.30007 12.5787 6.71036L14.2438 8.99029C14.4727 9.30791 14.853 9.31391 15.0852 8.99029L16.753 6.71635C17.0602 6.30007 16.9168 5.84668 16.3523 5.84668L15.56 5.84668C14.9168 2.51805 11.9749 0 8.45335 0ZM8.45335 14.4818C10.3495 14.4818 12.0595 13.7362 13.3127 12.5328C13.7665 12.1357 13.697 11.5641 13.3758 11.241C13.0989 10.9646 12.6472 10.8599 12.1683 11.2566C11.2142 12.1816 9.90049 12.7602 8.45335 12.7602C5.88526 12.7602 3.73186 11.0075 3.12343 8.63513L3.93008 8.63513C4.49177 8.63513 4.63239 8.18174 4.33397 7.77144L2.6689 5.49151C2.43998 5.17389 2.05966 5.1679 1.82754 5.49151L0.159677 7.76545C-0.147536 8.18174-0.01011 8.63513 0.56037 8.63513L1.34668 8.63513C1.98986 11.9638 4.93177 14.4818 8.45335 14.4818Z"
                                                  fill="white"
                                            />
                                        </g>
                                    </svg>
                                </div>
                            </Dropdown>

                            {/* Иконка системы */}
                            <Dropdown
                                menu={systemMenu}
                                placement="bottomRight"
                                arrow
                                trigger={['click']}
                                autoAdjustOverflow={false}
                                getPopupContainer={() => document.body}
                            >
                                <svg
                                    version="1.1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    viewBox="0 0 15.9777 16.2417"
                                    className="pos-app-header__status-icon"
                                    style={{ marginRight: '8px' }}
                                >
                                    <g>
                                        <rect height="16.2417" opacity="0" width="15.9777" x="0" y="0"/>
                                        <path d="M8.4528 12.8262C8.84096 13.0034 9.15507 13.3161 9.33296 13.7034L13.8679 13.7034C14.281 13.7034 14.6125 14.0437 14.6125 14.4508C14.6125 14.8639 14.281 15.1954 13.8679 15.1954L9.33337 15.1954C9.04986 15.8132 8.42429 16.2417 7.7054 16.2417C6.9865 16.2417 6.36094 15.8132 6.07743 15.1954L1.54566 15.1954C1.13261 15.1954 0.801055 14.8639 0.801055 14.4508C0.801055 14.0437 1.13261 13.7034 1.54566 13.7034L6.07784 13.7034C6.25617 13.3152 6.57136 13.0019 6.96079 12.8248L6.96079 11.5364L8.4528 11.5364Z"
                                              fill={systemIconColor}
                                        />
                                        <path d="M0 8.34207C0 10.2157 1.38025 11.5364 3.33135 11.5364L12.2773 11.5364C14.2284 11.5364 15.6086 10.2157 15.6086 8.34207C15.6086 7.81116 15.4712 7.30702 15.2623 6.83006L13.137 1.84946C12.6592 0.727692 11.7124 0.123269 10.426 0.123269L5.18816 0.123269C3.90779 0.123269 2.94901 0.722102 2.47124 1.84946L0.374289 6.78814C0.176554 7.24874 0 7.76365 0 8.34207ZM2.85895 5.12428L4.01432 2.24213C4.19172 1.78863 4.60845 1.54611 5.1918 1.54611L10.4224 1.54611C11.0117 1.54611 11.4225 1.78863 11.5999 2.24213L12.7525 5.12428C12.6042 5.12704 12.4048 5.14778 12.1571 5.14778L3.45432 5.14778C3.20661 5.14778 3.00722 5.12704 2.85895 5.12428ZM1.62535 8.34207C1.62535 7.40918 2.31215 6.78192 3.33135 6.78192L12.2773 6.78192C13.2965 6.78192 13.9833 7.40918 13.9833 8.34207C13.9833 9.32891 13.3564 9.90502 12.2773 9.90502L3.33135 9.90502C2.31215 9.90502 1.62535 9.27496 1.62535 8.34207ZM11.3353 8.34207C11.3353 8.83305 11.7388 9.23095 12.2242 9.22216C12.7123 9.21616 13.1098 8.83305 13.1098 8.34207C13.1098 7.87147 12.7035 7.4592 12.2242 7.4592C11.7476 7.4592 11.3353 7.87147 11.3353 8.34207Z"
                                              fill={systemIconColor}
                                        />
                                    </g>
                                </svg>
                            </Dropdown>

                            {/* Существующая иконка интернета */}
                            <Dropdown
                                menu={internetMenu}
                                placement="bottomRight"
                                arrow
                                trigger={['click']}
                                autoAdjustOverflow={false}
                                getPopupContainer={() => document.body}
                            >
                                <svg
                                    version="1.1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    viewBox="0 0 16.6644 11.8251"
                                    className="pos-app-header__status-icon"
                                >
                                    <g>
                                        <rect height="11.8251" opacity="0" width="16.6644" x="0" y="0"/>
                                        <path
                                            d="M1.11566 5.10034C1.27824 5.27171 1.53948 5.26892 1.70526 5.08876C3.41832 3.27862 5.64475 2.3298 8.14602 2.3298C10.6649 2.3298 12.9001 3.28461 14.6104 5.10034C14.7642 5.26013 15.0166 5.25134 15.1824 5.08276L16.1807 4.08761C16.3289 3.93342 16.3285 3.7277 16.2039 3.5795C14.5196 1.49175 11.3888 0.00878906 8.14602 0.00878906C4.90926 0.00878906 1.7784 1.49175 0.0881452 3.5795C-0.0364831 3.7277-0.0308934 3.93342 0.117307 4.08761Z"
                                            fill={iconColor}/>
                                        <path
                                            d="M4.01123 7.9576C4.19139 8.13816 4.44105 8.11738 4.61841 7.92843C5.46334 7.01158 6.79369 6.38274 8.14602 6.38873C9.51594 6.38274 10.8487 7.03515 11.7116 7.9576C11.8714 8.13496 12.1007 8.12337 12.2868 7.9516L13.4022 6.87614C13.5444 6.73673 13.5588 6.53619 13.4342 6.382C12.3264 5.06566 10.3345 4.11762 8.14602 4.11762C5.95755 4.11762 3.96567 5.06566 2.86387 6.382C2.73325 6.53619 2.74163 6.72474 2.89583 6.87614Z"
                                            fill={iconColor}/>
                                        <path
                                            d="M8.14882 11.8251C8.33817 11.8251 8.49516 11.7408 8.78759 11.46L10.5006 9.81165C10.6369 9.67823 10.666 9.45494 10.5386 9.30115C10.0253 8.65039 9.1336 8.1514 8.14882 8.1514C7.13447 8.1514 6.22604 8.67675 5.71311 9.37065C5.62323 9.50407 5.67236 9.68382 5.80618 9.81165L7.51005 11.46C7.80248 11.7348 7.96546 11.8251 8.14882 11.8251Z"
                                            fill={iconColor}/>
                                    </g>
                                </svg>
                            </Dropdown>
                            <Text className="pos-app-header__status-text">
                                {formatDate(currentTime)}
                            </Text>
                        </div>
                    )}

                    {/* User info block */}
                    <div className="pos-app-header__user-block">
                        {/* Аватарка пользователя с выпадающим меню */}
                        <Dropdown
                            menu={userMenu}
                            placement="bottomRight"
                            arrow
                            trigger={['click']}
                            autoAdjustOverflow={false}
                            getPopupContainer={() => document.body}
                        >
                            <div>
                                {/* Имя пользователя */}
                                <Text className="pos-app-header__user-name">
                                    {formatUserName(currentCashier)}
                                </Text>
                                <Avatar
                                    size={24}
                                    className={currentCashier ? "pos-app-header__avatar-user" : "pos-app-header__avatar-guest"}
                                >
                                    {currentCashier ? (
                                        getInitials(currentCashier)
                                    ) : (
                                        <UserOutlined/>
                                    )}
                                </Avatar>
                            </div>
                        </Dropdown>
                    </div>
                </div>

                {/* Right corner SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    className="pos-app-header__corner pos-app-header__corner--right"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 32V0H32C24.5602 0 20.8403 0 17.7883 0.817781C9.50613 3.03699 3.03699 9.50614 0.81778 17.7883C0 20.8403 0 24.5602 0 32Z"
                        fill="currentColor"
                    />
                </svg>
            </Header>
            <CalculatorModal
                visible={calculatorVisible}
                onClose={() => setCalculatorVisible(false)}
            />
            <ShiftInfoModal
                visible={shiftInfoVisible}
                onClose={() => setShiftInfoVisible(false)}
            />
            <CashTrackingModal
                visible={cashTrackingVisible}
                onClose={() => setCashTrackingVisible(false)}
            />
        </>
    );
};

export default PosAppHeader;